import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, RefreshCw, Trash2, Truck } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useTask } from '@/hooks/useTask'
import { useTaskKMCodes } from '@/hooks/useTaskEntities'
import { useTaskCountersWebSocket } from '@/hooks/useTaskCountersWebSocket'
import { useToast } from '@/hooks/useToast'
import { tasksApi } from '@/lib/api'
import { extractApiError } from '@/lib/api'
import { TASK_STATUS } from '@/lib/constants'
import { formatDateTime } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { TaskEntitiesPanel } from '@/components/features/tasks/TaskEntitiesPanel'
import { TaskDuplicateLogPanel } from '@/components/features/tasks/TaskDuplicateLogPanel'
import { PrintAgentBadge } from '@/components/features/tasks/PrintAgentBadge'
import { TaskInfoPanel } from '@/components/features/tasks/TaskInfoPanel'
import type { TaskStatus } from '@/types'

const CREATION_MODE_LABELS: Record<string, string> = {
  km_and_ka_file: 'КМ + КА из файлов',
  ka_file_only: 'Только КА из файлов',
  km_file_only: 'Только КМ из файла',
}

const TASK_STATUS_OPTIONS = Object.keys(TASK_STATUS) as TaskStatus[]

export function TaskDetailPage() {
  const { taskId: taskUuid } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: task, isLoading, refetch, isFetching } = useTask(taskUuid)
  const countersLive = useTaskCountersWebSocket(taskUuid)
  const kmQuery = useTaskKMCodes({
    taskUuid,
    tab: 'codes',
    activeTab: 'codes',
    page: 1,
    search: '',
  })
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCreatingShipment, setIsCreatingShipment] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const createMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!createMenuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setCreateMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [createMenuOpen])

  const handleCreateShipment = async () => {
    if (!taskUuid) return
    setCreateMenuOpen(false)
    setIsCreatingShipment(true)
    try {
      const { data } = await tasksApi.createShipment(taskUuid)
      const shipmentId = data.shipment?.id
      toast.success(
        shipmentId
          ? `Отгрузка ${shipmentId} создана (${data.additions_count ?? 0} поз.)`
          : 'Отгрузка создана',
      )
      if (shipmentId) {
        navigate(`/admin/shipments/${shipmentId}`)
      }
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsCreatingShipment(false)
    }
  }

  const handleDelete = async () => {
    if (!taskUuid) return
    if (!confirm(`Удалить задание ${task?.id || taskUuid}?`)) return

    setIsDeleting(true)
    try {
      await tasksApi.delete(taskUuid)
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.removeQueries({ queryKey: ['task', taskUuid] })
      toast.success('Задание удалено')
      navigate('/admin/tasks')
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleStatusChange = async (next: TaskStatus) => {
    if (!taskUuid || !task || next === task.status) return
    setIsUpdatingStatus(true)
    try {
      await tasksApi.update(taskUuid, { status: next })
      toast.success('Статус обновлён')
      await refetch()
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  if (isLoading) return <LoadingSpinner className="mt-20" />

  if (!task) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg">Задание не найдено</p>
        <Link to="/admin/tasks">
          <Button variant="ghost" className="mt-4">
            Назад к списку
          </Button>
        </Link>
      </div>
    )
  }

  const statusMeta = TASK_STATUS[task.status]
  const codesCount = Math.max(task.codes_count, kmQuery.data?.total ?? 0)

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-4 min-w-0">
          <Link to="/admin/tasks" className="mt-1">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">{task.id}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <select
                value={task.status}
                disabled={isUpdatingStatus}
                onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
                className={`text-xs font-medium rounded-full border-0 px-3 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 ${statusMeta.color} ${statusMeta.textColor}`}
                aria-label="Статус задания"
              >
                {TASK_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {TASK_STATUS[status].label}
                  </option>
                ))}
              </select>
              <PrintAgentBadge />
              {task.creation_mode && (
                <span className="text-xs text-gray-400">
                  {CREATION_MODE_LABELS[task.creation_mode] ?? task.creation_mode}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Создано: <span className="text-gray-700">{formatDateTime(task.created_at)}</span>
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="relative" ref={createMenuRef}>
            {task.creation_mode !== 'km_file_only' && (
            <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreateMenuOpen(open => !open)}
              isLoading={isCreatingShipment}
              aria-expanded={createMenuOpen}
              aria-haspopup="menu"
            >
              Создать на основании
              <ChevronDown className={`w-4 h-4 transition-transform ${createMenuOpen ? 'rotate-180' : ''}`} />
            </Button>
            {createMenuOpen && (
              <div
                role="menu"
                className="absolute z-50 top-full right-0 mt-1 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                  onClick={handleCreateShipment}
                >
                  <Truck className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>Отгрузка</span>
                </button>
              </div>
            )}
            </>
            )}
          </div>
          <Button variant="danger" size="sm" onClick={handleDelete} isLoading={isDeleting}>
            <Trash2 className="w-4 h-4 mr-1" /> Удалить
          </Button>
        </div>
      </div>

      <TaskInfoPanel task={task} />

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Номенклатура</h2>
        {(task.gtin_counts?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-400">Нет КМ в задании</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 w-16">№</th>
                  <th className="px-4 py-2">Наименование</th>
                  <th className="px-4 py-2 w-36 whitespace-nowrap">GTIN</th>
                  <th className="px-4 py-2 text-right w-36">Количество КМ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {task.gtin_counts!.map((row, idx) => (
                  <tr key={row.gtin} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-2">{row.product_name?.trim() || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs w-36 whitespace-nowrap">{row.gtin}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums w-36">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Счётчики
          <span
            className={`text-xs font-normal ${countersLive ? 'text-green-600' : 'text-gray-400'}`}
            title={countersLive ? 'Счётчик КМ обновляется по WebSocket' : 'WebSocket отключён, переподключение…'}
          >
            {countersLive ? '● live' : '○ offline'}
          </span>
        </h2>
        <div className={`grid gap-4 text-center ${task.creation_mode === 'km_file_only' ? 'grid-cols-1 max-w-xs mx-auto' : 'grid-cols-3'}`}>
          <div>
            <p className="text-gray-500 text-xs">Коды (КМ)</p>
            <p className="text-2xl font-bold text-green-600">{codesCount}</p>
            {(task.creation_mode === 'km_and_ka_file' || task.creation_mode === 'km_file_only') && (
              <p className="text-[11px] text-gray-500 mt-1">
                Напечатано: <span className="text-gray-700">{task.printed_km_count ?? 0}</span>/{codesCount}
              </p>
            )}
          </div>
          {task.creation_mode !== 'km_file_only' && (
          <>
          <div>
            <p className="text-gray-500 text-xs">Коробки</p>
            <p className="text-2xl font-bold text-cyan-600">{task.boxes_count}</p>
            {task.creation_mode === 'km_and_ka_file' && (
              <p className="text-[11px] text-gray-500 mt-1">
                КА коробок:{' '}
                <span className="text-gray-700">{task.printed_ka_box_count ?? 0}</span>/{task.boxes_count}
              </p>
            )}
          </div>
          <div>
            <p className="text-gray-500 text-xs">Паллеты</p>
            <p className="text-2xl font-bold text-blue-600">{task.pallets_count}</p>
            {task.creation_mode === 'km_and_ka_file' && (
              <p className="text-[11px] text-gray-500 mt-1">
                КА паллет:{' '}
                <span className="text-gray-700">{task.printed_ka_pallet_count ?? 0}</span>/{task.pallets_count}
              </p>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      <TaskEntitiesPanel task={task} />

      {task.creation_mode === 'ka_file_only' && <TaskDuplicateLogPanel taskUuid={task.uuid} />}
    </div>
  )
}
